<template>
  <div>
    <v-dialog v-model="open" :max-width="max_width" persistent>
      <v-card>
        <v-card-title>
          <span class="text-uppercase">{{ title }}</span>
        </v-card-title>
        <v-card-text>
          <div class="row mb-2">
            <div class="col-md-6">
              <v-btn
                color="blue-grey"
                class="mt-4"
                dark
                @click="exportToExcel"
                small
              >
                Exportar <font-awesome-icon icon="download"
              /></v-btn>
            </div>
            <div class="col-md-6">
              <v-text-field
                v-model="search"
                append-icon="search"
                label="Buscar"
                single-line
                hide-details
              ></v-text-field>
            </div>
          </div>
          <v-data-table
            :headers="headers"
            :items="items"
            :search="search"
            :loading="loading"
            loading-text="Cargando..."
            :options="{
              descending: true,
              itemsPerPage: 15,
            }"
            @pagination="updatePagination"
            :footer-props="{
              itemsPerPageOptions: [5, 10, 15, 50, -1],
            }"
          >
            <template v-slot:item="props">
              <tr>
                <td
                  v-for="(column, index) in headers"
                  :key="index"
                  :class="column.clickable ? 'redirect-modal' : ''"
                  @click="onCellClick(column, props.item)"
                >
                  {{ showField(column, props.item) }}
                </td>
              </tr>
            </template>
            <template v-if="has_totals" v-slot:[slot_body_append]>
              <tr class="grey lighten-3">
                <td v-for="(column, index) in headers" :key="'tot-' + index" class="font-weight-bold" :class="column.align == 'right' ? 'text-right' : ''">
                  {{ index == 0 ? "TOTAL" : totalOf(column) }}
                </td>
              </tr>
            </template>
            <template v-slot:no-results>
              <v-alert :value="true" color="error" icon="warning">
                {{ "Sin resultados - " + search }}
              </v-alert>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click="cancel()"> Cerrar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { formatUtils } from "@/libs/utils/formatUtils";
import * as XLSX from "xlsx";
import { excelUtils } from "@/libs/utils/excelUtils";

export default {
  props: {
    title: String,
    open: Boolean,
    cancel: Function,
    items: Array,
    headers: Array,
    max_width: {
      type: [String, Number],
      default: 800,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: "",
    // Slot dinamico: v-slot:body.append no es valido como modificador.
    slot_body_append: "body.append",
    formatUtils: formatUtils,
    rowsPerPageItems: [10, 20, 30, 40],
    pagination: {
      page: 1,
      itemsPerPage: 5,
    },
  }),
  computed: {
    // Los totales se muestran solo en las columnas marcadas con total: true en el header.
    has_totals() {
      return this.headers.some((column) => column.total);
    },
    // Mismo criterio que el filtro por defecto de la tabla, para que el total acompañe la busqueda.
    filtered_items() {
      const search = (this.search || "").toString().toLowerCase().trim();
      if (!search) return this.items;
      return this.items.filter((item) =>
        Object.keys(item).some((key) => String(item[key] == null ? "" : item[key]).toLowerCase().includes(search))
      );
    },
  },
  methods: {
    sumColumn(column, items) {
      return items.reduce((acc, item) => acc + (parseFloat(item[column.value]) || 0), 0);
    },
    // Totaliza lo que se está viendo: acompaña al filtro de búsqueda.
    totalOf(column) {
      if (!column.total) return "";
      const total = this.sumColumn(column, this.filtered_items);
      return column.format == "price" ? formatUtils.formatPrice(total) : total;
    },
    updatePagination(pagination) {
      this.pagination = pagination;
    },
    onCellClick(column, item) {
      if (!column.clickable) return;
      this.$emit("cell-click", { column: column, item: item });
    },
    showField(column, item) {
      if (item == null || column == null) return "";

      if (column.value != null) {
        if (column.format == "price")
          return formatUtils.formatPrice(item[column.value]);
        return item[column.value];
      }

      if (column.operation != undefined && column.operation != null) {
        switch (column.operation) {
          case "-": {
            let result = 0;
            for (let i = 0; i < column.fields.length; i++) {
              result -= item[column.fields[i]];
            }
            if (column.format == "price")
              return formatUtils.formatPrice(result);
            return result;
          }
        }
      }

      return "";
    },
    exportToExcel() {
      const worksheetData = this.items.map((item) =>
        this.headers.reduce((acc, column) => {
          acc[column.text] = this.showField(column, item);
          return acc;
        }, {})
      );

      if (this.has_totals) {
        worksheetData.push(
          this.headers.reduce((acc, column, index) => {
            if (index == 0) acc[column.text] = "TOTAL";
            else if (column.total) acc[column.text] = column.format == "price" ? formatUtils.formatPrice(this.sumColumn(column, this.items)) : this.sumColumn(column, this.items);
            else acc[column.text] = "";
            return acc;
          }, {})
        );
      }

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const range = XLSX.utils.decode_range(worksheet["!ref"]);

      // Los formatos salen del propio header: format 'date' o 'price' en cada columna.
      const date_columns = [];
      const price_columns = [];
      this.headers.forEach((column, index) => {
        if (column.format == "date") date_columns.push(index);
        if (column.format == "price") price_columns.push(index);
      });

      for (let row = range.s.r + 1; row <= range.e.r; ++row) {
        date_columns.forEach((col) => excelUtils.formatDateCell(worksheet, row, col));
        price_columns.forEach((col) => excelUtils.formatCurrencyCell(worksheet, row, col));
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");
      XLSX.writeFile(workbook, `${this.title || "export"}.xlsx`);
    },
  },
};
</script>
